// Pure helpers that turn the persisted messages array into a small,
// human-friendly insights model. No backend calls, no charts, no analytics —
// just enough signal to power a coaching screen.

import { SIGNAL_TO_LABEL, SIGNAL_TO_RISK } from "@/lib/toneMeta";

const STYLE_LABELS = {
    concise: "Concise",
    warm: "Warm",
    firm: "Firm",
    professional: "Professional",
    gentle: "Gentle",
};

export { STYLE_LABELS };

// Per-signal coaching tip — short, supportive, never judgmental.
const SIGNAL_TIPS = {
    accusatory:
        "Open with the impact, not the blame. Try “I noticed…” instead of “you always…”.",
    dismissive:
        "Acknowledge the other side first. A single sentence of recognition softens a lot.",
    passive_aggressive:
        "Say what you actually want, plainly. Indirect notes invite defensive replies.",
    threatening:
        "Replace ultimatums with a clear request and a reason. People respond better to needs than to threats.",
    contemptuous:
        "Drop the comparisons. State your point directly without judging the person.",
    frustrated:
        "Name the impact, not the irritation. “This is blocking me…” lands better than “again?”.",
    defensive:
        "Lead with what's true before what's unfair. It keeps the door open.",
    abrupt:
        "Add one line of context or a soft opener. Even five words can change how it reads.",
    cold:
        "A short greeting or one warm word makes the same message feel collaborative.",
    pressuring:
        "Share the deadline and the why. Urgency without context can feel forceful.",
    neutral:
        "Your tone reads neutral and clear — keep going.",
    constructive:
        "You're framing things collaboratively — that's a strength worth doubling down on.",
    appreciative:
        "Appreciative messages build trust. Specific thanks land even harder.",
    apologetic:
        "Owning mistakes plainly, without over-apologising, keeps your credibility intact.",
    empathetic:
        "Acknowledging how someone feels makes the hardest messages land softer.",
    assertive:
        "Clear and respectful at the same time — that's a high-skill register. Keep it.",
    solution_focused:
        "Naming a concrete next step turns concerns into momentum.",
};

// Pull the relevant signal/risk/style from a stored bubble, regardless of
// whether it was sent as the original or as a rewrite.
function extractFromBubble(b) {
    const signal = b.signal || b.originalSignal || null;
    const risk = b.risk || b.originalRisk || (signal ? SIGNAL_TO_RISK[signal] : null);
    const style = b.style || null;
    return { signal, risk, style };
}

// Time-of-day bucket for a bubble's createdAt. Used by weekly insights.
function bucketOfHour(h) {
    if (h >= 5 && h < 12) return "morning";
    if (h >= 12 && h < 18) return "afternoon";
    if (h >= 18 && h < 23) return "evening";
    return "late";
}

// Compute weekly / monthly / yearly time-based insights from the persisted
// bubble timestamps. Pure aggregation — no chart data, just 1–2 short
// human-readable lines per scope.
function computeTimeInsights(messages) {
    const out = { weekly: null, monthly: null, yearly: null };
    if (!messages || messages.length === 0) return out;

    const now = Date.now();
    const DAY = 24 * 60 * 60 * 1000;
    const week = now - 7 * DAY;
    const recent30 = now - 30 * DAY;
    const prev30 = now - 60 * DAY;

    // Index analyzed messages by their timestamps + signal/risk
    const items = [];
    for (const m of messages) {
        const t = m.createdAt ? Date.parse(m.createdAt) : NaN;
        if (Number.isNaN(t)) continue;
        const { signal, risk, style } = extractFromBubble(m);
        if (!signal && !risk && !style) continue;
        const d = new Date(t);
        items.push({
            t,
            signal,
            risk,
            style,
            hour: d.getHours(),
            dow: d.getDay(),
        });
    }
    if (items.length === 0) return out;

    // ----- Weekly: last 7 days, look for tone-of-day patterns -----
    const last7 = items.filter((x) => x.t >= week);
    if (last7.length >= 3) {
        const buckets = { morning: [], afternoon: [], evening: [], late: [] };
        for (const x of last7) buckets[bucketOfHour(x.hour)].push(x);

        // Find the bucket with the highest "needs_softening + risky" rate.
        let worstKey = null;
        let worstRate = 0;
        let worstSignal = null;
        for (const [k, arr] of Object.entries(buckets)) {
            if (arr.length < 2) continue; // tiny samples don't make a pattern
            const risky = arr.filter(
                (x) => x.risk === "risky" || x.risk === "needs_softening",
            );
            const rate = risky.length / arr.length;
            if (rate > worstRate) {
                worstRate = rate;
                worstKey = k;
                // pick the most common signal in that bucket
                const counts = {};
                for (const x of risky) {
                    if (x.signal) counts[x.signal] = (counts[x.signal] || 0) + 1;
                }
                worstSignal = Object.entries(counts).sort(
                    (a, b) => b[1] - a[1],
                )[0]?.[0];
            }
        }

        // Find the bucket with the highest "good" rate.
        let bestKey = null;
        let bestRate = 0;
        for (const [k, arr] of Object.entries(buckets)) {
            if (arr.length < 2) continue;
            const good = arr.filter((x) => x.risk === "good");
            const rate = good.length / arr.length;
            if (rate > bestRate) {
                bestRate = rate;
                bestKey = k;
            }
        }

        const bucketWord = {
            morning: "weekday mornings",
            afternoon: "afternoons",
            evening: "evenings",
            late: "late at night",
        };

        const lines = [];
        if (worstKey && worstRate >= 0.5 && worstSignal) {
            lines.push(
                `Your ${bucketWord[worstKey]} tend to read more ${
                    SIGNAL_TO_LABEL[worstSignal]?.toLowerCase() ||
                    worstSignal.replace("_", " ")
                }.`,
            );
        }
        if (bestKey && bestRate >= 0.5 && bestKey !== worstKey) {
            lines.push(`Your ${bucketWord[bestKey]} read calmer and clearer.`);
        }
        if (lines.length === 0) {
            lines.push("Your week reads steady — no strong time-of-day pattern yet.");
        }
        out.weekly = lines.slice(0, 2);
    }

    // ----- Monthly: compare last 30 days vs the 30 days before -----
    const recent = items.filter((x) => x.t >= recent30);
    const previous = items.filter((x) => x.t >= prev30 && x.t < recent30);
    if (recent.length >= 4 && previous.length >= 4) {
        const riskyRate = (arr) =>
            arr.filter((x) => x.risk === "risky").length / arr.length;
        const r0 = riskyRate(previous);
        const r1 = riskyRate(recent);
        const drop = r0 - r1;

        // Style trend: compare top style between halves.
        const topStyle = (arr) => {
            const c = {};
            for (const x of arr) if (x.style) c[x.style] = (c[x.style] || 0) + 1;
            const top = Object.entries(c).sort((a, b) => b[1] - a[1])[0];
            return top ? top[0] : null;
        };
        const sPrev = topStyle(previous);
        const sNow = topStyle(recent);

        const lines = [];
        if (drop >= 0.1) {
            lines.push("High-risk messages are down compared to last month.");
        } else if (drop <= -0.1) {
            lines.push("A few more high-risk messages slipped in this month.");
        }
        if (sPrev && sNow && sPrev !== sNow) {
            lines.push(
                `Your rewrites are leaning more ${STYLE_LABELS[sNow]?.toLowerCase() || sNow} lately (was ${STYLE_LABELS[sPrev]?.toLowerCase() || sPrev}).`,
            );
        }
        if (lines.length === 0) {
            lines.push("Your tone has stayed consistent across the last month.");
        }
        out.monthly = lines.slice(0, 2);
    }

    // ----- Yearly: only when the data spans at least 90 days -----
    const oldest = Math.min(...items.map((x) => x.t));
    const span = now - oldest;
    if (span >= 90 * DAY && items.length >= 12) {
        // Compare first half vs second half of the entire history
        const sorted = items.slice().sort((a, b) => a.t - b.t);
        const half = Math.floor(sorted.length / 2);
        const early = sorted.slice(0, half);
        const late = sorted.slice(half);
        const goodRate = (arr) =>
            arr.filter((x) => x.risk === "good").length / arr.length;
        const ge = goodRate(early);
        const gl = goodRate(late);
        const lines = [];
        if (gl - ge >= 0.1) {
            lines.push(
                "Your communication has grown more balanced and empathetic over time.",
            );
        } else if (ge - gl >= 0.1) {
            lines.push(
                "Things feel a bit sharper than they used to — worth a gentle reset.",
            );
        } else {
            lines.push("Your tone stays remarkably steady year over year.");
        }
        out.yearly = lines.slice(0, 1);
    }

    return out;
}

function topNFromCounts(counts, n = 3) {
    return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, n)
        .map(([key, count]) => ({ key, count }));
}

export function computeInsights(messages = []) {
    const riskCounts = { risky: 0, needs_softening: 0, good: 0 };
    const signalCounts = {}; // every detected signal across analyzed messages
    const styleCounts = {};  // every rewrite style the user has chosen
    let analyzedCount = 0;
    let rewrittenCount = 0;

    for (const m of messages || []) {
        const { signal, risk, style } = extractFromBubble(m);
        if (signal) {
            analyzedCount += 1;
            signalCounts[signal] = (signalCounts[signal] || 0) + 1;
        }
        if (risk && riskCounts[risk] !== undefined) {
            riskCounts[risk] += 1;
        }
        if (style) {
            rewrittenCount += 1;
            styleCounts[style] = (styleCounts[style] || 0) + 1;
        }
    }

    const topSignals = topNFromCounts(signalCounts, 3);
    const topStyles = topNFromCounts(styleCounts, 3);

    const topSignal = topSignals[0]
        ? {
              signal: topSignals[0].key,
              label: SIGNAL_TO_LABEL[topSignals[0].key] || topSignals[0].key,
              count: topSignals[0].count,
              tip: SIGNAL_TIPS[topSignals[0].key] || null,
          }
        : null;

    // Sorted, fully-labeled list — every signal the user has produced, no collapsing.
    const signalBreakdown = Object.entries(signalCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([key, count]) => ({
            signal: key,
            label: SIGNAL_TO_LABEL[key] || key,
            risk: SIGNAL_TO_RISK[key] || "neutral",
            count,
        }));

    // Sorted, fully-labeled list of every rewrite style the user has used.
    const styleBreakdown = Object.entries(styleCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([key, count]) => ({
            style: key,
            label: STYLE_LABELS[key] || key,
            count,
        }));

    // Weekly insight: a one-liner derived from the dominant signal + risk mix.
    let weeklyInsight = null;
    if (analyzedCount === 0) {
        weeklyInsight =
            "Check a few messages to see patterns in how you communicate.";
    } else if (riskCounts.risky === 0 && riskCounts.needs_softening === 0) {
        weeklyInsight =
            "Your recent messages read calm and clear — you're doing this well.";
    } else if (topSignal) {
        const friendly = topSignal.label.toLowerCase();
        weeklyInsight = `You often sound ${friendly}. ${topSignal.tip || "Small softening can change how it lands."}`;
    } else {
        weeklyInsight = "Keep checking your tone — patterns will surface here soon.";
    }

    const stylesLabeled = topStyles
        .map(({ key }) => STYLE_LABELS[key] || key)
        .filter(Boolean);

    return {
        analyzedCount,
        rewrittenCount,
        totalMessages: (messages || []).length,
        riskCounts,
        topSignal,
        topStyles: stylesLabeled, // ordered, top-3 max (kept for backwards compat)
        signalBreakdown,          // every signal with counts (no collapsing)
        styleBreakdown,           // every rewrite style with counts
        weeklyInsight,
        timeInsights: computeTimeInsights(messages),
    };
}

// =========================================================================
// Personalized coaching insights (strict, rule-driven).
//
// Every candidate must be triggered by an explicit data condition. No
// fallback / "we don't have enough data yet" copy — if no strong signal
// exists, we return an empty array and the dashboard simply hides the
// coach section. This keeps the insights honest and avoids generic lines.
// =========================================================================

// Positive effect phrases for dominant REWRITE STYLES (Rule A). Only populated
// for styles that carry a constructive framing — we never describe a harsh
// style as "helpful". If the rewrite style isn't here, Rule A is skipped.
const STYLE_POSITIVE_EFFECT = {
    warm: "this helps build trust and keeps conversations open",
    firm: "this makes your point clear without losing respect",
    professional: "this makes your message easy to take seriously",
    gentle: "this keeps even difficult moments feeling connected",
    concise: "this keeps your notes direct and easy to reply to",
};

// Suggested complementary tone when a style is rarely used (Rule B). Only
// evaluated when the user has a meaningful rewrite history.
const STYLE_BALANCE_NOTE = {
    warm: "Adding a little warmth in some messages could make them feel more inviting.",
    firm: "Adding a firmer tone in tougher moments could make your position clearer.",
    gentle: "A gentler tone in personal messages could make them land softer.",
    professional: "A more professional register could help in workplace threads.",
    concise: "A tighter, more concise line could work better for quick back-and-forth.",
};

// Rule F — the "abrupt / harsh" family of detected signals.
const HARSH_SIGNALS = new Set([
    "abrupt",
    "accusatory",
    "contemptuous",
    "dismissive",
    "threatening",
    "cold",
]);

// FNV-1a-ish short hash — stable, non-crypto, just for dedupe.
function insightHash(text) {
    let h = 2166136261;
    for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = (h * 16777619) >>> 0;
    }
    return h.toString(36);
}

// a/an article for a word (approximate, purely orthographic).
function article(word) {
    return /^[aeiou]/i.test(word || "") ? "an" : "a";
}

export function computePersonalInsights(messages = [], shownHashes = [], counters = {}) {
    const candidates = [];
    if (!messages || messages.length === 0) return [];

    const now = Date.now();
    const DAY = 24 * 60 * 60 * 1000;

    // --- Base aggregates -------------------------------------------------
    const signalCounts = {};
    const styleCounts = {};
    const intentCounts = {};
    let analyzed = 0;     // ≈ message_reviewed
    let refined = 0;      // ≈ message_refined (rewrite chosen)
    const recent7 = [];
    const prev7 = [];
    const recent14 = [];

    for (const m of messages) {
        const { signal, risk, style } = extractFromBubble(m);
        if (signal) {
            analyzed += 1;
            signalCounts[signal] = (signalCounts[signal] || 0) + 1;
        }
        if (style) {
            refined += 1;
            styleCounts[style] = (styleCounts[style] || 0) + 1;
        }
        if (m.intent) intentCounts[m.intent] = (intentCounts[m.intent] || 0) + 1;

        const t = m.createdAt ? Date.parse(m.createdAt) : NaN;
        if (!Number.isNaN(t)) {
            const row = { ...m, _t: t, _risk: risk, _signal: signal, _style: style };
            if (t >= now - 14 * DAY) recent14.push(row);
            if (t >= now - 7 * DAY) recent7.push(row);
            else if (t >= now - 14 * DAY) prev7.push(row);
        }
    }

    // ===================================================================
    // Rule A — Dominant REWRITE STYLE > 60%
    // ===================================================================
    if (refined >= 5) {
        const [topStyle, topStyleCount] =
            Object.entries(styleCounts).sort((a, b) => b[1] - a[1])[0] || [];
        if (topStyle && STYLE_POSITIVE_EFFECT[topStyle]) {
            const ratio = topStyleCount / refined;
            if (ratio > 0.6) {
                const label = STYLE_LABELS[topStyle] || topStyle;
                candidates.push({
                    type: "pattern",
                    text: `You tend to rely on ${article(label)} ${label.toLowerCase()} style — ${STYLE_POSITIVE_EFFECT[topStyle]}.`,
                    priority: 95,
                });
            }
        }
    }

    // ===================================================================
    // Rule B — Rewrite-style imbalance (< 10% for a named style)
    // Only meaningful once the user has a decent rewrite history.
    // ===================================================================
    if (refined >= 10) {
        // Find the single most-underused style with a balance note.
        let worst = null;
        for (const style of Object.keys(STYLE_BALANCE_NOTE)) {
            const c = styleCounts[style] || 0;
            const ratio = c / refined;
            if (ratio < 0.1) {
                if (!worst || ratio < worst.ratio) {
                    worst = { style, ratio };
                }
            }
        }
        if (worst) {
            candidates.push({
                type: "imbalance",
                text: STYLE_BALANCE_NOTE[worst.style],
                priority: 60,
            });
        }
    }

    // ===================================================================
    // Rule C — High refinement rate (message_refined / message_reviewed > 40%)
    // ===================================================================
    if (analyzed >= 5) {
        const refineRate = refined / analyzed;
        if (refineRate > 0.4) {
            candidates.push({
                type: "refinement",
                text: "You often refine messages before sending — that's a strong sign of careful intent.",
                priority: 75,
            });
        }
    }

    // ===================================================================
    // Rule D — Low copy rate (message_copied / message_reviewed < 40%)
    // ===================================================================
    const copyCount = Number(counters.message_copied || 0);
    if (analyzed >= 5 && copyCount > 0) {
        const copyRate = copyCount / analyzed;
        if (copyRate < 0.4) {
            candidates.push({
                type: "copy_gap",
                text: "You don't always go with the first suggestion — fine-tuning the rewrite further might help match your voice.",
                priority: 65,
            });
        }
    }

    // ===================================================================
    // Rule E — Refinement rate decreasing week-over-week
    // (user's drafts are landing better without needing rewrites)
    // ===================================================================
    if (recent7.length >= 4 && prev7.length >= 4) {
        const refineRateFor = (arr) =>
            arr.filter((x) => x._style).length / arr.length;
        const rNow = refineRateFor(recent7);
        const rPrev = refineRateFor(prev7);
        if (rPrev - rNow >= 0.2) {
            candidates.push({
                type: "trend",
                text: "You're needing fewer refinements recently — your tone instincts are getting sharper.",
                priority: 100,
            });
        }
    }

    // ===================================================================
    // Rule F — > 30% of messages flagged as abrupt/harsh
    // ===================================================================
    if (analyzed >= 5) {
        let harshCount = 0;
        for (const [sig, n] of Object.entries(signalCounts)) {
            if (HARSH_SIGNALS.has(sig)) harshCount += n;
        }
        const harshRate = harshCount / analyzed;
        if (harshRate > 0.3) {
            candidates.push({
                type: "risk",
                text: "Some messages still come across as a bit abrupt — softening slightly could improve responses.",
                priority: 85,
            });
        }
    }

    // ===================================================================
    // Rule G — Intent/tone contrast: Set boundary + warm/gentle
    // ===================================================================
    let boundaryCalm = 0;
    let boundaryTotal = 0;
    for (const m of messages) {
        if (m.intent !== "set_boundary" || !m.style) continue;
        boundaryTotal += 1;
        if (m.style === "warm" || m.style === "gentle") boundaryCalm += 1;
    }
    if (boundaryTotal >= 3 && boundaryCalm / boundaryTotal >= 0.6) {
        candidates.push({
            type: "contrast",
            text: "You tend to set boundaries in a calm way — this helps keep relationships intact while still being clear.",
            // Intent-specific, so prioritize above generic dominant-style pattern.
            priority: 105,
        });
    }

    // If no rule fired, stay silent — the dashboard hides the section.
    if (candidates.length === 0) return [];

    // --- Anti-repeat (demote, don't suppress) ---------------------------
    const seen = new Set(shownHashes || []);
    for (const c of candidates) {
        c.hash = insightHash(`${c.type}|${c.text}`);
        if (seen.has(c.hash)) c.priority *= 0.55;
    }

    // --- Rank + pick top 2 across distinct types ------------------------
    candidates.sort((a, b) => b.priority - a.priority);
    const chosen = [];
    const usedTypes = new Set();
    for (const c of candidates) {
        if (chosen.length >= 2) break;
        if (!usedTypes.has(c.type)) {
            chosen.push(c);
            usedTypes.add(c.type);
        }
    }
    return chosen;
}

// =========================================================================
// FEATURE 1 — COMMUNICATION MIRROR (timeline-aware reflection)
//
// Returns a calm, 1-2 sentence reflection appropriate for the given scope
// (weekly | monthly | yearly). Strictly rule-driven, no LLM calls, no
// therapy language, no behavioral profiling. If there isn't enough data,
// returns `null` and the UI hides the section.
// =========================================================================

const SCOPE_TO_DAYS = { weekly: 7, monthly: 30, yearly: 365 };
const SCOPE_LABEL = { weekly: "this week", monthly: "this month", yearly: "this year" };
const MIRROR_CACHE_KEY = "toneguard:mirrorCache";

function _withinScope(messages, scope) {
    const days = SCOPE_TO_DAYS[scope];
    if (!days) return [];
    const cutoff = Date.now() - days * 86400000;
    return (messages || []).filter((m) => {
        const t = m.createdAt ? Date.parse(m.createdAt) : NaN;
        return !Number.isNaN(t) && t >= cutoff;
    });
}

/**
 * Pure, lightweight reflection generator. The output is intentionally
 * generic and reflective — never diagnostic.
 */
export function computeMirrorReflection(messages = [], scope = "weekly") {
    const win = _withinScope(messages, scope);
    if (win.length < 3) return null;

    let analyzed = 0;
    let refined = 0;
    const riskCounts = { risky: 0, needs_softening: 0, good: 0 };
    const styleCounts = {};

    for (const m of win) {
        const { signal, risk, style } = extractFromBubble(m);
        if (signal) {
            analyzed += 1;
            if (risk && riskCounts[risk] !== undefined) riskCounts[risk] += 1;
        }
        if (style) {
            refined += 1;
            styleCounts[style] = (styleCounts[style] || 0) + 1;
        }
    }

    if (analyzed < 3) return null;

    const refineRate = analyzed > 0 ? refined / analyzed : 0;
    const calmShare =
        analyzed > 0 ? riskCounts.good / analyzed : 0;
    const topStyle = Object.entries(styleCounts).sort(
        (a, b) => b[1] - a[1],
    )[0]?.[0];

    const lines = [];

    // Primary reflection — calmness trajectory
    if (calmShare >= 0.6) {
        lines.push(
            `Your messages have read clearly and calmly ${SCOPE_LABEL[scope]} — that steadiness shows.`,
        );
    } else if (refineRate >= 0.4) {
        lines.push(
            `You often paused to rewrite emotionally charged messages ${SCOPE_LABEL[scope]} — a quiet sign of care.`,
        );
    } else if (calmShare < 0.3 && riskCounts.risky >= 2) {
        lines.push(
            `Some messages ran a little hot ${SCOPE_LABEL[scope]} — softening one or two could shift how they land.`,
        );
    } else {
        lines.push(
            `Your communication has been steady and considered ${SCOPE_LABEL[scope]}.`,
        );
    }

    // Optional secondary — tone preference
    if (topStyle && refined >= 4) {
        const styleLabel = STYLE_LABELS[topStyle] || topStyle;
        lines.push(
            `You leaned on a ${styleLabel.toLowerCase()} voice most often.`,
        );
    }

    return { scope, primary: lines[0], secondary: lines[1] || null };
}

// =========================================================================
// FEATURE 2 — BEFORE vs AFTER EMOTIONAL IMPACT
//
// Maps every rewrite the user picked into a small set of human-readable
// indicators. Predefined heuristics, no scoring, no LLM. Returns total
// improvements + indicator counts + the most recent before/after pair as
// an illustrative example.
// =========================================================================

const HARSH_SIGNAL_SET = new Set([
    "accusatory",
    "contemptuous",
    "dismissive",
    "threatening",
    "passive_aggressive",
    "frustrated",
    "defensive",
    "abrupt",
    "cold",
    "pressuring",
]);

function _impactIndicatorsFor(message) {
    // A rewritten message contributes to multiple indicators based on the
    // original signal it came from + the chosen style.
    const out = new Set();
    const fromSignal = message.originalSignal || null;
    const fromRisk = message.originalRisk || null;
    const style = message.style || null;

    if (fromRisk === "risky") out.add("escalation_reduced");
    if (HARSH_SIGNAL_SET.has(fromSignal)) out.add("softer_delivery");
    if (style === "professional" || style === "concise") out.add("clearer");
    if (style === "warm" || style === "gentle") out.add("constructive");
    if (
        fromRisk === "needs_softening" &&
        (style === "professional" || style === "firm")
    ) {
        out.add("balanced");
    }

    return [...out];
}

const INDICATOR_LABELS = {
    escalation_reduced: "Escalation Reduced",
    clearer: "Clearer Communication",
    constructive: "More Constructive",
    balanced: "Emotionally Balanced",
    softer_delivery: "Softer Delivery",
};

export function computeBeforeAfterImpact(messages = []) {
    const improved = (messages || []).filter(
        (m) => m.variant === "improved" && m.originalText,
    );
    if (improved.length === 0) return null;

    const counts = {
        escalation_reduced: 0,
        clearer: 0,
        constructive: 0,
        balanced: 0,
        softer_delivery: 0,
    };
    let lastPair = null;
    let lastTime = -Infinity;

    for (const m of improved) {
        for (const k of _impactIndicatorsFor(m)) counts[k] += 1;
        const t = m.createdAt ? Date.parse(m.createdAt) : NaN;
        if (!Number.isNaN(t) && t > lastTime) {
            lastTime = t;
            const fromLabel = m.originalSignal
                ? (SIGNAL_TO_LABEL[m.originalSignal] || m.originalSignal)
                : "Original";
            const toLabel = m.style
                ? `Calm & ${STYLE_LABELS[m.style] || m.style}`
                : "Calmer";
            lastPair = {
                before: { text: m.originalText, label: fromLabel },
                after: { text: m.text, label: toLabel },
            };
        }
    }

    const indicators = Object.entries(counts)
        .filter(([, n]) => n > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([key, n]) => ({ key, label: INDICATOR_LABELS[key], count: n }));

    if (indicators.length === 0 && !lastPair) return null;

    return {
        totalImprovements: improved.length,
        indicators,
        latestPair: lastPair,
    };
}

// =========================================================================
// FEATURE 3 — EMOTIONAL HEATMAP (observational, pattern-based)
//
// Up to 2 short observations derived from time-of-day and day-of-week
// patterns. Strictly observational copy — never predictive or invasive.
// =========================================================================

export function computeHeatmapObservations(messages = []) {
    const win = (messages || []).filter((m) => {
        const t = m.createdAt ? Date.parse(m.createdAt) : NaN;
        return !Number.isNaN(t);
    });
    if (win.length < 6) return [];

    // Bucket each message into (timeOfDay, dayOfWeek).
    const totalByBucket = { morning: 0, afternoon: 0, evening: 0, night: 0 };
    const riskyByBucket = { morning: 0, afternoon: 0, evening: 0, night: 0 };
    let weekdayTotal = 0;
    let weekendTotal = 0;
    let weekdayStyleCount = { professional: 0, firm: 0, warm: 0, gentle: 0 };
    let weekendStyleCount = { professional: 0, firm: 0, warm: 0, gentle: 0 };

    for (const m of win) {
        const d = new Date(m.createdAt);
        const bucket = bucketOfHour(d.getHours());
        totalByBucket[bucket] += 1;
        const { risk, style } = extractFromBubble(m);
        if (risk === "risky" || risk === "needs_softening") {
            riskyByBucket[bucket] += 1;
        }
        const isWeekend = [0, 6].includes(d.getDay());
        if (isWeekend) {
            weekendTotal += 1;
            if (style && weekendStyleCount[style] !== undefined) {
                weekendStyleCount[style] += 1;
            }
        } else {
            weekdayTotal += 1;
            if (style && weekdayStyleCount[style] !== undefined) {
                weekdayStyleCount[style] += 1;
            }
        }
    }

    const observations = [];

    // 1. Late-night charged-tone pattern.
    if (totalByBucket.night >= 3) {
        const nightHotRate = riskyByBucket.night / totalByBucket.night;
        const dayTotal =
            totalByBucket.morning + totalByBucket.afternoon + totalByBucket.evening;
        const dayHot =
            riskyByBucket.morning + riskyByBucket.afternoon + riskyByBucket.evening;
        const dayHotRate = dayTotal > 0 ? dayHot / dayTotal : 0;
        if (nightHotRate - dayHotRate >= 0.2) {
            observations.push({
                key: "night_charged",
                text: "Late-night messages tend to read a little more emotionally charged.",
            });
        }
    }

    // 2. Weekday Professional vs Weekend Warm/Gentle.
    if (weekdayTotal >= 4 && weekendTotal >= 2) {
        const proRate =
            (weekdayStyleCount.professional + weekdayStyleCount.firm) /
            Math.max(1, weekdayTotal);
        const warmRate =
            (weekendStyleCount.warm + weekendStyleCount.gentle) /
            Math.max(1, weekendTotal);
        if (proRate >= 0.4) {
            observations.push({
                key: "weekday_professional",
                text: "Your professional tone shows up most during weekdays.",
            });
        } else if (warmRate >= 0.4) {
            observations.push({
                key: "weekend_warm",
                text: "Weekend messages tend to feel warmer and more personal.",
            });
        }
    }

    // 3. Evening softening pattern.
    if (totalByBucket.evening >= 3 && totalByBucket.afternoon >= 3) {
        const evHot = riskyByBucket.evening / totalByBucket.evening;
        const noonHot = riskyByBucket.afternoon / totalByBucket.afternoon;
        if (noonHot - evHot >= 0.2 && observations.length < 2) {
            observations.push({
                key: "evening_calmer",
                text: "Your evenings read calmer than your afternoons.",
            });
        }
    }

    return observations.slice(0, 2);
}

// Lightweight localStorage caching helper — used by the dashboard so we
// don't recompute the same scope on every focus/visit. TTL: 4 hours.
const MIRROR_CACHE_TTL_MS = 4 * 60 * 60 * 1000;

export function getCachedMirror(scope) {
    try {
        const raw = window.localStorage.getItem(MIRROR_CACHE_KEY);
        if (!raw) return null;
        const blob = JSON.parse(raw);
        const entry = blob && blob[scope];
        if (!entry || !entry.computedAt) return null;
        if (Date.now() - entry.computedAt > MIRROR_CACHE_TTL_MS) return null;
        return entry.value;
    } catch {
        return null;
    }
}

export function setCachedMirror(scope, value) {
    try {
        const raw = window.localStorage.getItem(MIRROR_CACHE_KEY);
        const blob = raw ? JSON.parse(raw) : {};
        blob[scope] = { computedAt: Date.now(), value };
        window.localStorage.setItem(MIRROR_CACHE_KEY, JSON.stringify(blob));
    } catch {
        /* silent */
    }
}
